import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Modal, Form, Input, DatePicker, Space, message, Popconfirm, Calendar, Tooltip, Upload, Alert, List, Typography, Select, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, InboxOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import ViewToggle from "../../components/ui/ViewToggle";
import { regionName, regionOptions } from "../../helper/regions";

// "ALL" stands for holidays that apply to every region (no region set).
const ALL = "ALL";

const Holidays = () => {
  const { request } = useRequest();
  const [holidays, setHolidays] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [view, setView] = useState("list");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importRegion, setImportRegion] = useState(undefined);
  const [regionFilter, setRegionFilter] = useState([]); // region codes and/or ALL
  const REGIONS = useMemo(() => regionOptions(), []);
  const FILTER_OPTIONS = useMemo(() => [{ value: ALL, label: "All regions (every employee)" }, ...REGIONS], [REGIONS]);
  const regionLabel = (code) => (code === ALL ? "All regions" : regionName(code));
  const shown = regionFilter.length ? holidays.filter((h) => (h.region ? regionFilter.includes(h.region) : regionFilter.includes(ALL))) : holidays;

  const fetchHolidays = () => {
    request({ url: apiPath.holidays, method: "GET", onSuccess: (res) => setHolidays(res?.data?.data || []) });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchHolidays, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ region: regionFilter.length === 1 && regionFilter[0] !== ALL ? regionFilter[0] : undefined }); // pre-select the region being viewed
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ ...record, region: record.region || undefined, date: record.date ? moment(record.date) : null });
    setModalOpen(true);
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      const payload = { ...values, region: values.region || "", date: values.date.format("YYYY-MM-DD") };
      const url = editing ? `${apiPath.holidays}/${editing._id}` : apiPath.holidays;
      const method = editing ? "PUT" : "POST";
      request({
        url,
        method,
        data: payload,
        onSuccess: () => {
          message.success(editing ? "Holiday updated" : "Holiday created");
          setModalOpen(false);
          fetchHolidays();
        },
      });
    });
  };

  const onDelete = (record) => {
    request({
      url: `${apiPath.holidays}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Holiday deleted");
        fetchHolidays();
      },
    });
  };

  const openImport = () => {
    setImportFile(null);
    setImportResult(null);
    setImportRegion(regionFilter.length === 1 && regionFilter[0] !== ALL ? regionFilter[0] : undefined);
    setImportModalOpen(true);
  };

  const downloadTemplate = () => {
    const csv = "Name,Date,Description,Region (optional)\nRepublic Day,2027-01-26,National holiday,IN\nHoli,2027-03-04,Festival of colors,IN\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "holiday_import_template.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const onImportSubmit = () => {
    if (!importFile) {
      message.error("Please select an Excel or CSV file to import");
      return;
    }
    const formData = new FormData();
    formData.append("file", importFile);
    formData.append("region", importRegion || ""); // empty = holidays for every region
    setImporting(true);
    request({
      url: apiPath.holidaysImport,
      method: "POST",
      data: formData,
      header: { "Content-Type": "multipart/form-data" },
      onSuccess: (res) => {
        setImporting(false);
        // useRequest's onSuccess already passes the unwrapped response body
        // (see request()'s `onSuccess(response.data)`), and the backend's
        // import result is a plain object ({created, skipped, errors}), not
        // an array — ResponseHelper.ok only auto-nests a second `data` layer
        // for array payloads. So this needs res?.data, not res?.data?.data
        // (same bug class as PayRun's summary modal).
        setImportResult(res?.data || { created: [], skipped: [], errors: [] });
        if (res?.data?.created?.length) fetchHolidays();
      },
      onError: (err) => {
        setImporting(false);
        message.error(err?.response?.data?.message || "Could not import holidays");
      },
    });
  };

  const dateCellRender = (value) => {
    const dateStr = value.format("YYYY-MM-DD");
    const matches = shown.filter((h) => h.date === dateStr);
    const isPast = !value.isSameOrAfter(moment(), "day");
    return (
      <div className="i-ems-calendar-cell-content">
        {matches.map((h) => (
          <Tooltip key={h._id} title={`${h.name} · ${h.region ? regionName(h.region) : "All regions"}`}>
            <div className="i-ems-calendar-tag" style={{ color: "#6366F1", background: "#6366F11A" }}>
              {h.region ? `${h.region} · ` : ""}
              {h.name}
            </div>
          </Tooltip>
        ))}
        {!isPast && (
          <div className="i-ems-calendar-apply-wrap">
            <Button
              size="small"
              type="primary"
              className="i-ems-calendar-apply-btn"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openCreate();
                form.setFieldsValue({ date: value });
              }}
            >
              Add
            </Button>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Date", dataIndex: "date" },
    {
      title: "Region",
      dataIndex: "region",
      width: 190,
      render: (r) => (r ? <Tag color="geekblue">{regionName(r)}</Tag> : <Tag>All regions</Tag>),
    },
    { title: "Description", dataIndex: "description" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Delete this holiday?" onConfirm={() => onDelete(record)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader
        title="Holidays"
        subtitle="Manage the company holiday calendar"
        actions={
          <Space>
            <ViewToggle value={view} onChange={setView} />
            <Button icon={<UploadOutlined />} onClick={openImport}>
              Import Excel/CSV
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add Holiday
            </Button>
          </Space>
        }
      />
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount={0}
          maxTagPlaceholder={(omitted) => `${omitted.length} selected`}
          placeholder="All calendars (every region)"
          style={{ width: 280 }}
          value={regionFilter}
          onChange={(v) => setRegionFilter(v || [])}
          options={FILTER_OPTIONS}
        />
        {regionFilter.map((code) => (
          <Tag key={code} closable onClose={() => setRegionFilter((f) => f.filter((c) => c !== code))} style={{ margin: 0, padding: "3px 10px", borderRadius: 14, background: "#EEF2FF", borderColor: "#C7D2FE", color: "#3730A3" }}>
            Region: <b>{regionLabel(code)}</b>
          </Tag>
        ))}
        {regionFilter.length ? (
          <Button type="link" size="small" onClick={() => setRegionFilter([])}>
            Clear all
          </Button>
        ) : null}
        <span style={{ color: "#6B7280", fontSize: 12, marginLeft: "auto" }}>
          {shown.length} of {holidays.length} holidays
        </span>
      </div>
      {view === "list" ? (
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={shown}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: <EmptyState message="No holidays added yet" actionLabel="Add Holiday" onAction={openCreate} /> }}
        />
      ) : (
        <div className="i-ems-apply-calendar">
          <Calendar dateCellRender={dateCellRender} />
        </div>
      )}

      <Modal title={editing ? "Edit Holiday" : "Add Holiday"} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={onSubmit} destroyOnClose>
        <Form layout="vertical" form={form}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="region" label="Region" extra="Only employees whose region matches will see this holiday. Leave empty for every employee.">
            <Select allowClear showSearch optionFilterProp="label" placeholder="All regions" options={REGIONS} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Import Holidays from Excel/CSV"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        footer={
          importResult ? (
            <Button type="primary" onClick={() => setImportModalOpen(false)}>
              Done
            </Button>
          ) : (
            [
              <Button key="cancel" onClick={() => setImportModalOpen(false)}>
                Cancel
              </Button>,
              <Button key="submit" type="primary" loading={importing} onClick={onImportSubmit}>
                Import
              </Button>,
            ]
          )
        }
        destroyOnClose
      >
        {!importResult ? (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="First column Name, second column Date, third column Description (optional). First row is treated as a header and skipped. Dates can be a real Excel date cell, or text like 2027-01-26, 26/01/2027 or 26 Jan 2027. An optional fourth column Region (a country code such as HK) overrides the region chosen below for that row."
            />
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>This calendar is for</div>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="All regions (every employee)"
                style={{ width: "100%" }}
                value={importRegion}
                onChange={setImportRegion}
                options={REGIONS}
              />
              <div style={{ color: "#6B7280", fontSize: 12, marginTop: 4 }}>
                Pick a region, for example Hong Kong, and only employees in that region will see these holidays.
              </div>
            </div>
            <Upload.Dragger
              beforeUpload={(file) => {
                setImportFile(file);
                return false;
              }}
              onRemove={() => setImportFile(null)}
              maxCount={1}
              accept=".xlsx,.xls,.csv"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag an Excel/CSV file here</p>
            </Upload.Dragger>
            <Button type="link" icon={<DownloadOutlined />} onClick={downloadTemplate} style={{ paddingLeft: 0, marginTop: 8 }}>
              Download template
            </Button>
            <div style={{ color: "#8C8C8C", fontSize: 12 }}>
              PDF holiday lists aren't supported directly — copy the dates into the template above first.
            </div>
          </>
        ) : (
          <div>
            <Alert
              type={importResult.errors?.length ? "warning" : "success"}
              showIcon
              message={`Imported ${importResult.created?.length || 0} holiday${importResult.created?.length === 1 ? "" : "s"}${
                importResult.skipped?.length ? `, skipped ${importResult.skipped.length} duplicate${importResult.skipped.length === 1 ? "" : "s"}` : ""
              }${importResult.errors?.length ? `, ${importResult.errors.length} row${importResult.errors.length === 1 ? "" : "s"} had errors` : ""}`}
              style={{ marginBottom: 16 }}
            />
            {!!importResult.errors?.length && (
              <>
                <Typography.Text strong>Rows with errors</Typography.Text>
                <List
                  size="small"
                  dataSource={importResult.errors}
                  renderItem={(e) => (
                    <List.Item>
                      Row {e.row}: {e.reason}
                    </List.Item>
                  )}
                  style={{ marginBottom: 16, maxHeight: 160, overflowY: "auto" }}
                />
              </>
            )}
            {!!importResult.skipped?.length && (
              <>
                <Typography.Text strong>Skipped (already exist)</Typography.Text>
                <List
                  size="small"
                  dataSource={importResult.skipped}
                  renderItem={(s) => (
                    <List.Item>
                      Row {s.row}: {s.date} — {s.reason}
                    </List.Item>
                  )}
                  style={{ maxHeight: 160, overflowY: "auto" }}
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </PageTransition>
  );
};

export default Holidays;
